import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  IconButton,
  Tooltip,
  Alert,
  GridLegacy as Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhCN } from 'date-fns/locale';

interface BillingRecord {
  _id: string;
  paymentId: string;
  parkingSession: {
    sessionId: string;
    vehicle: {
      licensePlate: string;
      vehicleInfo: {
        type: string;
        color: string;
      };
    };
    parkingSpot: {
      spotNumber: string;
      floor: string;
      zone: string;
      type: string;
    };
  };
  amount: {
    original: number;
    discount: number;
    tax: number;
    final: number;
  };
  paymentMethod: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    username: string;
    email: string;
  };
}

interface BillingStatistics {
  overall: {
    totalRevenue: number;
    totalTransactions: number;
    averageAmount: number;
    successfulPayments: number;
    failedPayments: number;
    pendingPayments: number;
  };
  paymentMethods: Array<{
    _id: string;
    count: number;
    revenue: number;
  }>;
  timeSeries: Array<{
    _id: any;
    revenue: number;
    transactions: number;
  }>;
}

const BillingManagement: React.FC = () => {
  const [records, setRecords] = useState<BillingRecord[]>([]);
  const [statistics, setStatistics] = useState<BillingStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // 篩選狀態
  const [filters, setFilters] = useState({
    status: '',
    paymentMethod: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    licensePlate: '',
    minAmount: '',
    maxAmount: ''
  });
  
  // 對話框狀態
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BillingRecord | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // 狀態選項
  const statusOptions = [
    { value: 'pending', label: '待處理', color: 'warning' },
    { value: 'processing', label: '處理中', color: 'info' },
    { value: 'completed', label: '已完成', color: 'success' },
    { value: 'failed', label: '失敗', color: 'error' },
    { value: 'cancelled', label: '已取消', color: 'default' },
    { value: 'refunded', label: '已退款', color: 'secondary' }
  ];

  // 支付方式選項
  const paymentMethodOptions = [
    { value: 'cash', label: '現金' },
    { value: 'credit_card', label: '信用卡' },
    { value: 'debit_card', label: '借記卡' },
    { value: 'mobile_payment', label: '移動支付' },
    { value: 'bank_transfer', label: '銀行轉帳' },
    { value: 'digital_wallet', label: '數字錢包' }
  ];

  // 載入收費記錄
  const loadBillingRecords = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      // 添加篩選條件
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.paymentMethod) queryParams.append('paymentMethod', filters.paymentMethod);
      if (filters.startDate) queryParams.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) queryParams.append('endDate', filters.endDate.toISOString());
      if (filters.licensePlate) queryParams.append('licensePlate', filters.licensePlate);
      if (filters.minAmount) queryParams.append('minAmount', filters.minAmount);
      if (filters.maxAmount) queryParams.append('maxAmount', filters.maxAmount);

      const response = await fetch(`/api/billing/records?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('載入收費記錄失敗');
      }

      const data = await response.json();
      setRecords(data.data.records);
      setTotalPages(data.data.pagination.totalPages);
      setTotalRecords(data.data.pagination.totalRecords);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入收費記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  // 載入統計數據
  const loadStatistics = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) queryParams.append('endDate', filters.endDate.toISOString());

      const response = await fetch(`/api/billing/statistics?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      }
    } catch (err) {
      console.error('載入統計數據失敗:', err);
    }
  };

  // 查看記錄詳情
  const handleViewDetail = (record: BillingRecord) => {
    setSelectedRecord(record);
    setDetailDialog(true);
  };

  // 編輯記錄狀態
  const handleEditStatus = (record: BillingRecord) => {
    setSelectedRecord(record);
    setEditStatus(record.status);
    setEditNotes('');
    setEditDialog(true);
  };

  // 更新記錄狀態
  const handleUpdateStatus = async () => {
    if (!selectedRecord) return;

    try {
      const response = await fetch(`/api/billing/records/${selectedRecord.paymentId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: editStatus,
          notes: editNotes
        })
      });

      if (!response.ok) {
        throw new Error('更新狀態失敗');
      }

      setEditDialog(false);
      loadBillingRecords();
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新狀態失敗');
    }
  };

  // 導出記錄
  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const queryParams = new URLSearchParams({ format });
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.paymentMethod) queryParams.append('paymentMethod', filters.paymentMethod);
      if (filters.startDate) queryParams.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) queryParams.append('endDate', filters.endDate.toISOString());

      const response = await fetch(`/api/billing/export?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('導出失敗');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-records-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : '導出失敗');
    }
  };

  // 重置篩選
  const handleResetFilters = () => {
    setFilters({
      status: '',
      paymentMethod: '',
      startDate: null,
      endDate: null,
      licensePlate: '',
      minAmount: '',
      maxAmount: ''
    });
    setCurrentPage(1);
  };

  // 獲取狀態顏色
  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.color || 'default';
  };

  // 獲取支付方式標籤
  const getPaymentMethodLabel = (method: string) => {
    const option = paymentMethodOptions.find(opt => opt.value === method);
    return option?.label || method;
  };

  useEffect(() => {
    loadBillingRecords();
    loadStatistics();
  }, [currentPage, filters]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhCN}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          收費管理
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 統計卡片 */}
        {statistics && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    總收入
                  </Typography>
                  <Typography variant="h5">
                    ¥{statistics.overall.totalRevenue.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    總交易數
                  </Typography>
                  <Typography variant="h5">
                    {statistics.overall.totalTransactions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    平均金額
                  </Typography>
                  <Typography variant="h5">
                    ¥{statistics.overall.averageAmount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    成功率
                  </Typography>
                  <Typography variant="h5">
                    {statistics.overall.totalTransactions > 0 
                      ? ((statistics.overall.successfulPayments / statistics.overall.totalTransactions) * 100).toFixed(1)
                      : 0}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* 篩選器 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>狀態</InputLabel>
                  <Select
                    value={filters.status}
                    label="狀態"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">全部</MenuItem>
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>支付方式</InputLabel>
                  <Select
                    value={filters.paymentMethod}
                    label="支付方式"
                    onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value })}
                  >
                    <MenuItem value="">全部</MenuItem>
                    {paymentMethodOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="開始日期"
                  value={filters.startDate}
                  onChange={(date) => setFilters({ ...filters, startDate: date as Date | null })}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="結束日期"
                  value={filters.endDate}
                  onChange={(date) => setFilters({ ...filters, endDate: date as Date | null })}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="車牌號碼"
                  value={filters.licensePlate}
                  onChange={(e) => setFilters({ ...filters, licensePlate: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={() => setCurrentPage(1)}
                  >
                    篩選
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                  >
                    重置
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadBillingRecords}
            disabled={loading}
          >
            刷新
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('csv')}
          >
            導出CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('json')}
          >
            導出JSON
          </Button>
        </Box>

        {/* 記錄表格 */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>付款ID</TableCell>
                <TableCell>車牌號碼</TableCell>
                <TableCell>停車位</TableCell>
                <TableCell>金額</TableCell>
                <TableCell>支付方式</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>創建時間</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record._id}>
                  <TableCell>{record.paymentId}</TableCell>
                  <TableCell>
                    {record.parkingSession?.vehicle?.licensePlate || '-'}
                  </TableCell>
                  <TableCell>
                    {record.parkingSession?.parkingSpot?.spotNumber || '-'}
                  </TableCell>
                  <TableCell>¥{record.amount.final.toFixed(2)}</TableCell>
                  <TableCell>
                    {getPaymentMethodLabel(record.paymentMethod)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusOptions.find(opt => opt.value === record.status)?.label || record.status}
                      color={getStatusColor(record.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(record.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="查看詳情">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetail(record)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="編輯狀態">
                      <IconButton
                        size="small"
                        onClick={() => handleEditStatus(record)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 分頁 */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
          />
        </Box>

        {/* 詳情對話框 */}
        <Dialog
          open={detailDialog}
          onClose={() => setDetailDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>收費記錄詳情</DialogTitle>
          <DialogContent>
            {selectedRecord && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">付款ID</Typography>
                  <Typography>{selectedRecord.paymentId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">車牌號碼</Typography>
                  <Typography>
                    {selectedRecord.parkingSession?.vehicle?.licensePlate || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">停車位</Typography>
                  <Typography>
                    {selectedRecord.parkingSession?.parkingSpot?.spotNumber || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">原始金額</Typography>
                  <Typography>¥{selectedRecord.amount.original.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">折扣</Typography>
                  <Typography>¥{selectedRecord.amount.discount.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">最終金額</Typography>
                  <Typography>¥{selectedRecord.amount.final.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">支付方式</Typography>
                  <Typography>
                    {getPaymentMethodLabel(selectedRecord.paymentMethod)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">狀態</Typography>
                  <Chip
                    label={statusOptions.find(opt => opt.value === selectedRecord.status)?.label || selectedRecord.status}
                    color={getStatusColor(selectedRecord.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">創建時間</Typography>
                  <Typography>
                    {new Date(selectedRecord.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">更新時間</Typography>
                  <Typography>
                    {new Date(selectedRecord.updatedAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialog(false)}>關閉</Button>
          </DialogActions>
        </Dialog>

        {/* 編輯狀態對話框 */}
        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>編輯記錄狀態</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>狀態</InputLabel>
                  <Select
                    value={editStatus}
                    label="狀態"
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="備註"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>取消</Button>
            <Button onClick={handleUpdateStatus} variant="contained">
              更新
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default BillingManagement;